import { noop } from '@sgme/fp';
import { Tooltip } from '@sgme/ui';
import type { LifeCyclePaginatedProducts, LifeCycleProduct } from 'apiConnector/discover/lifeCycle';
import { lifeCycleApiConnector } from 'apiConnector/discover/lifeCycle';
import { usePostTradeContent } from 'components/discover/post-trade/hooks';
import { DISCOVER_DEFAULT_TECHNICAL_ERROR_KEY } from 'components/discover/shared';
import { Pagination } from 'components/shared/Blotter/Pagination/Pagination';
import { FormattedMessage } from 'components/shared/Intl';
import { useAppSelector } from 'hooks/useAppSelector';
import type { IntlKey } from 'locales';
import { range } from 'lodash-es';
import { FC, MouseEvent, ReactNode, useCallback, useEffect, useState } from 'react';
import { AccordionBody, AccordionItem } from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';
import { useDispatch, useSelector } from 'react-redux';
import { type Observable } from 'rxjs';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import {
  lifecyclePageChanged,
  lifecyclePaginationChanged,
  lifecycleSizeAndPageChanged,
} from 'state/discover/page/page.actions';
import type { LifeCycleCategory } from 'state/discover/page/page.model';
import { getLifecycleSectionPagination } from 'state/discover/page/page.selector';
import type { AppState } from 'state/state.model';
import { getLifecyclePortalUrl } from 'state/user';
import styled from 'styled-components';
import { httpClient } from 'utils/http/httpClient';
import type { HttpError } from 'utils/http/httpClient.model';
import { LoadableContent } from 'utils/status';

import { ErrorWithRetry } from '../../../shared/ErrorWithRetry';
import { ProductListEntry, ProductListEntrySkeleton } from './product-list-entry';

const StyledTooltipDiv = styled.div`
  max-width: 500px !important;
  min-width: 200px !important;
`;

type LifeCycleTitle =
  | 'nearToMaturity'
  | 'nearToCapitalBarrier'
  | 'nearToAutocall'
  | 'withCapitalBarrierEvent'
  | 'withNoMoreOptionValue';

interface PropsWithApi {
  id: string;
  onClick: (event: MouseEvent) => void;
  isActive?: boolean;
  title: LifeCycleTitle;
  category: LifeCycleCategory;
  fetchData(pageNumber: number, pageSize: number): Observable<LifeCyclePaginatedProducts<LifeCycleProduct>>;

  children(product: LifeCycleProduct, index: number): ReactNode;

  openInSecondary: (isin: string) => void;
}
export const LifeCycleSectionContainer: FC<PropsWithApi> = ({
  id,
  title,
  isActive = false,
  onClick,
  fetchData,
  children,
  openInSecondary,
  category,
}) => {
  const [products, setProducts] = useState<LoadableContent<LifeCycleProduct[], 'auth' | 'unknown'>>({ status: 'idle' });
  const pagination = useAppSelector(s => getLifecycleSectionPagination(s, category));
  const dispatch = useDispatch();
  const lifecyclePortalUrl = useSelector((state: AppState) => getLifecyclePortalUrl(state)) || '#';
  const { content, hasAuthError, hasUnknownError, isEmptyContent, isLoading } = usePostTradeContent(products);
  useEffect(() => {
    if (products.status !== 'idle') {
      return;
    }
    setProducts({ status: 'loading' });
    fetchData(pagination.pageNumber, pagination.pageSize).subscribe({
      next({ data: responseData, pagination: responsePagination }) {
        setProducts({ status: 'success', content: responseData });
        dispatch(lifecyclePaginationChanged(category, responsePagination));
      },
      error({ status }: HttpError) {
        setProducts({ status: 'error', error: status === 401 || status === 403 ? 'auth' : 'unknown' });
      },
    });
  }, [category, fetchData, pagination.pageNumber, pagination.pageSize, dispatch, products.status]);

  const handleRetry = () => {
    setProducts({ status: 'idle' });
  };

  const onPageChanged = useCallback(
    (page: number) => {
      setProducts({ status: 'idle' });
      dispatch(lifecyclePageChanged(category, page - 1));
    },
    [dispatch, category],
  );
  const onSizeChanged = useCallback(
    (size: number) => {
      setProducts({ status: 'idle' });
      dispatch(lifecycleSizeAndPageChanged(category, size, 0));
    },
    [dispatch, category],
  );

  const [titleTranslationKey, titleTooltipTanslationKey] = getTranslationKeys(title);

  if (hasAuthError) {
    throw new Error('Not authorized to access Lifecyle API');
  }
  if (isEmptyContent) {
    return null;
  }

  return (
    <AccordionItem
      eventKey={id}
      as={'li'}
      id={id}
      data-e2e="life-cycle-item"
      className="list-group-item border-bottom px-0 clickable"
      data-is-active={isActive}
      onClick={onClick}
    >
      <div className="py-2 fw-semibold d-flex flex-row" data-e2e="life-cycle-item-title">
        {content && (
          <>
            <div className="flex-grow-1">
              <FormattedMessage id={titleTranslationKey} defaultMessage={title} />
              <Tooltip
                style={{ padding: 0 }}
                contents={
                  <StyledTooltipDiv className="text-secondary px-3 py-2 shadow-sm bg-lvl1">
                    <FormattedMessage id={titleTooltipTanslationKey} defaultMessage={title} tagName="span" />
                  </StyledTooltipDiv>
                }
              >
                <span id={`discover_lifeCycle_${title}_badge_total`} className="ms-1" data-e2e="life-cycle-item-total">
                  {`(${pagination.total})`}
                </span>
              </Tooltip>
            </div>
            <em className="icon icon-sm text-secondary flex-grow-0">
              {isActive ? 'keyboard_arrow_down' : 'keyboard_arrow_right'}
            </em>
          </>
        )}
        {isLoading && <Skeleton width="15rem" />}
      </div>

      {hasUnknownError && (
        <ErrorWithRetry
          onRetry={handleRetry}
          e2e="life-cycle-item-error"
          msgKey={DISCOVER_DEFAULT_TECHNICAL_ERROR_KEY}
        />
      )}

      {content && (
        <AccordionBody className="px-0">
          <ul className="list-group list-group-flush ms-3">
            {content.map((product, index) => (
              <ProductListEntry
                key={product.productKey}
                trackOpenProductDetail={trackOpenProductDetail}
                lifecyclePortalUrl={lifecyclePortalUrl}
                product={product}
                openInSecondary={openInSecondary}
              >
                {children(product, index)}
              </ProductListEntry>
            ))}
          </ul>
          <Pagination
            className="justify-content-center"
            page={pagination.pageNumber + 1}
            itemsPerPage={pagination.pageSize}
            totalItems={pagination.total}
            hasSummary={false}
            showEntriesLabelVisible={false}
            siblingsMaxCnt={1}
            onPageChanged={onPageChanged}
            availableItemsPerPage={[3, 5, 10]}
            onSizeChanged={onSizeChanged}
            hideOnCurrentItemsLowerThanPageSize
          />
        </AccordionBody>
      )}
      {isLoading && isActive && (
        <AccordionBody className="px-0">
          <ul className="list-group list-group-flush ms-3">
            {range(5).map((key: number) => (
              <ProductListEntrySkeleton key={key} />
            ))}
          </ul>
          <Pagination
            className="justify-content-center"
            page={pagination.pageNumber + 1}
            itemsPerPage={pagination.pageSize}
            totalItems={pagination.total}
            hasSummary={false}
            showEntriesLabelVisible={false}
            siblingsMaxCnt={1}
            onPageChanged={noop}
          />
        </AccordionBody>
      )}
    </AccordionItem>
  );
};

function getTranslationKeys(title: LifeCycleTitle): [IntlKey, IntlKey] {
  switch (title) {
    case 'nearToMaturity':
      return ['discover.lifeCycle.nearToMaturity', 'discover.lifeCycle.nearToMaturity.totalBadge.tooltip'];
    case 'nearToCapitalBarrier':
      return ['discover.lifeCycle.nearToCapitalBarrier', 'discover.lifeCycle.nearToCapitalBarrier.totalBadge.tooltip'];
    case 'nearToAutocall':
      return ['discover.lifeCycle.nearToAutocall', 'discover.lifeCycle.nearToAutocall.totalBadge.tooltip'];
    case 'withCapitalBarrierEvent':
      return [
        'discover.lifeCycle.withCapitalBarrierEvent',
        'discover.lifeCycle.withCapitalBarrierEvent.totalBadge.tooltip',
      ];
    case 'withNoMoreOptionValue':
      return [
        'discover.lifeCycle.withNoMoreOptionValue',
        'discover.lifeCycle.withNoMoreOptionValue.totalBadge.tooltip',
      ];
  }
}

function trackOpenProductDetail() {
  logAnalytics.trackEvent('Discover', 'Open Lifecycle product detail');
}

export const lifeCycleApi = lifeCycleApiConnector(httpClient);
export type LifeCycleItemProps = Omit<PropsWithApi, 'title' | 'fetchData' | 'children'>;
